import { log } from "../../../";

import * as React from "react";
import { observable, action } from "mobx";
import { observer } from "mobx-react";

import CategoryMenuStore from "../../../stores/CategoryMenuStore";

// Material UI
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";

import { StringUtils } from "../../../core/utils/StringUtils";
import * as CustomLists from "../../../models/customLists";
import { Languages, AudioClipVoice } from "../../../models";
import { Colors } from "../../../core/utils/Colors";
import { Fonts } from "../../../core/utils/Fonts";

// Localization
import { strings } from "../../../core/localizedStrings";
import LanguageDropdown from "components/LanguageDropdown";

var Row = require("react-flexbox-grid").Row;
var Col = require("react-flexbox-grid").Col;

export interface CategoryEditorDetailsProps extends React.Props<any> {
    category?: CustomLists.Category;
    store: CategoryMenuStore;
}

@observer
export class CategoryEditorDetails extends React.Component<CategoryEditorDetailsProps> {
    protected textInput: any;

    @observable private title: string = "";
    @observable private keywordToAdd: string = "";
    @observable private audioClipVoice = AudioClipVoice.ENGLISH_US;

    @observable private audioClipVoice2: AudioClipVoice;
    @observable private isPublic: boolean;
    @observable private isTextField2LanguageSame: boolean = true;

    constructor(props: any, context: any) {
        super(props, context);

        if (this.props.category.title !== undefined) {
            this.title = this.props.category.title;
        }

        this.isPublic = this.props.category.public;
        if (this.props.category.voice) {
            this.audioClipVoice = this.props.category.voice;
        }

        if (this.props.category.answersVoice) {
            this.audioClipVoice2 = this.props.category.answersVoice;
        }

        if (this.audioClipVoice !== this.audioClipVoice2) {
            this.isTextField2LanguageSame = false;
        }
    }

    public componentDidMount() {
        this.textInput = document.getElementById("titleTextField");
        if (this.textInput !== undefined) {
            setTimeout(() => {
                this.textInput.focus();
            }, 200);
        }
    }

    public render() {
        return this.renderEditableDetails();
    }

    private onKeyPressed = (event: any) => {
        if (event.keyCode === 13) {
            this.onOkClicked();
        }
    };

    @action
    private onListTitleChange = (event: any) => {
        this.title = event.currentTarget.value;
    };

    @action
    private onKeywordChange = (event: any) => {
        this.keywordToAdd = event.currentTarget.value;
    };

    private onKeywordTextFieldKeyDown = (event: any) => {
        if (event.keyCode === 13) {
            this.handleKeywordAdd();
        }
    };

    @action
    private handleLanguageChange = (language: AudioClipVoice) => {
        this.audioClipVoice = language;

        if (this.isTextField2LanguageSame) {
            this.audioClipVoice2 = this.audioClipVoice;
        }
    };

    @action
    private handleLanguageChange2 = (language: AudioClipVoice) => {
        this.audioClipVoice2 = language;
    };

    @action
    private handleAccessRadioChange = (event: any, value: any) => {
        this.isPublic = value === "public";
    };

    @action
    private handleSameLanguageCheckbox = (event: any, value: any) => {
        this.isTextField2LanguageSame = value;

        if (this.isTextField2LanguageSame) {
            this.audioClipVoice2 = this.audioClipVoice;
        }
    };

    @action
    private handleKeywordDelete = (keywordIndex: number) => {
        log.verbose(`Removing keyword: ${this.props.category.keywords[keywordIndex]}`);
        this.props.category.keywords.splice(keywordIndex, 1);

        // Only update if it has already been added to the DB
        if (this.props.category.key) {
            this.props.store.rootStore.createListsStore.updateCategory(this.props.category);
        } else {
            this.forceUpdate();
        }
    };

    @action
    private handleKeywordAdd = () => {
        if (this.keywordToAdd !== undefined && this.keywordToAdd.length > 0) {
            log.verbose(`Adding keyword: ${this.keywordToAdd}`);
            this.props.category.keywords.push(this.keywordToAdd.trim().toLowerCase());
            this.keywordToAdd = "";

            if (this.props.category.key !== undefined) {
                this.props.store.rootStore.createListsStore.updateCategory(this.props.category);
            }
        }
    };

    @action
    private onOkClicked = () => {
        if (StringUtils.isEmptyOrUndefined(this.title)) {
            this.props.store.rootStore.showInfoMessage({ text: strings.titleCannotBeEmptyInfoMessage });
            return;
        }

        let title = this.title.trim();
        let uid = this.props.store.rootStore.userStore.user.uid;
        this.props.category.uid = uid;
        this.props.category.title = title;
        this.props.category.public = this.isPublic;

        this.props.category.language = Languages.voiceToLanguageType(this.audioClipVoice);
        this.props.category.voice = this.audioClipVoice;

        if (this.audioClipVoice2) {
            this.props.category.answersLanguage = Languages.voiceToLanguageType(this.audioClipVoice2);
            this.props.category.answersVoice = this.audioClipVoice2;
        }

        // If admin and on site list, don't use uid
        let tab = this.props.store.rootStore.browseListsStore.currentTab;
        if (tab === CustomLists.TabSelection.SITE && this.props.store.rootStore.userStore.user.isAdmin) {
            this.props.category.uid = "site";
            this.props.category.profileImage = require(`../../../assets/images/icon.png`);
        }

        this.props.store.rootStore.createListsStore.updateCategory(this.props.category);
        this.props.store.shouldShowCategoryDetailsEditor = false;
    };

    private renderEditableDetails() {
        let languageLabel1 = strings.audioClipLanguageLabel;
        let languageLabel2 = strings.audioClipLanguageLabel + " " + strings.textField2Label;

        if (this.props.category.isExpressionCategory()) {
            languageLabel1 += " " + strings.textField1Label;
        }
        return (
            <div style={{ margin: 40 }}>
                <Row>
                    <TextField
                        id="titleTextField"
                        value={this.title}
                        onChange={this.onListTitleChange}
                        fullWidth={true}
                        inputProps={{ style: { fontFamily: Fonts.BalsamiqSans, fontSize: 24 } }}
                        label={strings.listTitleLabel}
                        onKeyDown={this.onKeyPressed}
                    />
                </Row>
                <Row>
                    <Col style={{ paddingRight: 20, paddingTop: 20 }}>
                        <LanguageDropdown
                            label={languageLabel1}
                            intialLanguage={this.audioClipVoice}
                            onChange={this.handleLanguageChange}
                        />
                    </Col>
                    {this.props.category.isExpressionCategory() ? (
                        <Col style={{ paddingTop: 20 }}>
                            <Row>
                                <LanguageDropdown
                                    label={languageLabel2}
                                    intialLanguage={this.audioClipVoice2}
                                    onChange={this.handleLanguageChange2}
                                    disabled={this.isTextField2LanguageSame}
                                />
                            </Row>
                            <Row>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.isTextField2LanguageSame}
                                            style={{ fontSize: 12 }}
                                            onChange={this.handleSameLanguageCheckbox}
                                        />
                                    }
                                    label={strings.sameAsTextField1Label}
                                />
                            </Row>
                        </Col>
                    ) : null}
                </Row>
                <Row>
                    <div style={{ paddingTop: 25 }}>
                        <Typography variant="caption">{strings.keywordsLabel}</Typography>
                    </div>
                </Row>
                <Row>
                    <div style={{ display: "flex", flexWrap: "wrap" }}>{this.renderChips()}</div>
                </Row>
                <Row>
                    <Col>
                        <div style={{ minHeight: 60, paddingTop: 10 }}>
                            <TextField
                                placeholder={strings.addKeyword}
                                value={this.keywordToAdd}
                                onChange={this.onKeywordChange}
                                onKeyDown={this.onKeywordTextFieldKeyDown}
                                style={{ marginTop: -10, maxWidth: 200, fontSize: 16 }}
                            />
                            <Button variant="contained" onClick={this.handleKeywordAdd} style={{ marginLeft: 10 }}>
                                {strings.addWithPlus}
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row
                    style={{ display: "block", paddingTop: 5, paddingBottom: 3, fontSize: 12, color: Colors.darkGrey }}
                >
                    <Typography variant="caption">{strings.listAccessibility}</Typography>
                </Row>
                <Row>
                    <FormControl>
                        <RadioGroup
                            name="access"
                            value={this.isPublic ? "public" : "private"}
                            onChange={this.handleAccessRadioChange}
                        >
                            <FormControlLabel
                                style={{ marginTop: -5 }}
                                value="public"
                                control={<Radio />}
                                label={strings.public}
                            />
                            <FormControlLabel
                                style={{ marginTop: -20 }}
                                value="private"
                                control={<Radio />}
                                label={strings.private}
                            />
                        </RadioGroup>
                    </FormControl>
                </Row>
                <Row end="xs" style={{ paddingTop: 20 }}>
                    <Button variant="contained" onClick={this.onOkClicked} onKeyDown={this.onKeyPressed}>
                        {strings.ok}
                    </Button>
                </Row>
            </div>
        );
    }

    private renderChips() {
        return this.props.category.keywords.map((keyword: string, index: number) => (
            <Chip
                label={keyword}
                key={index}
                onDelete={() => this.handleKeywordDelete(index)}
                style={{ paddingBottom: 4, fontFamily: Fonts.BalsamiqSans, margin: "4px 4px 0px 0px" }}
            />
        ));
    }
}
